import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { HeroSection } from "@/components/home/HeroSection";
import { Marquee } from "@/components/site/Marquee";
import { Reveal, MaskHeading } from "@/components/site/Reveal";
import { SectionHead } from "@/components/site/PageHero";
import { BrandPortfolioGrid } from "@/components/site/BrandCard";
import { BrandStructure } from "@/components/site/BrandStructure";
import { BusinessPillars } from "@/components/site/BusinessPillars";
import { EnquiryForm } from "@/components/site/EnquiryForm";
import { ContactSection } from "@/components/site/ContactSection";
import { BRANDS, ENQUIRY_TYPES } from "@/config/site";
import { usePageMeta } from "@/hooks/usePageMeta";

const Intro = () => (
  <section className="py-24 md:py-36" data-testid="intro-section">
    <div className="container-x grid gap-16 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Reveal><p className="eyebrow">01 — The Group</p></Reveal>
        <MaskHeading lines={["One Group.", <em key="m" className="italic font-light text-rose-ink">Multiple</em>, "Consumer Categories."]} className="font-display mt-5 text-4xl leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl" />
        <Reveal delay={0.15}>
          <p className="mt-8 max-w-lg text-base leading-relaxed text-[var(--ink-2)] md:text-lg">
            Suvirika Retail Pvt Ltd brings together a portfolio of businesses serving different consumer needs and market segments. From B2B distribution and affordable beauty to fashion, accessories, and everyday household essentials, each brand is developed with a distinct purpose and audience.
          </p>
          <Link to="/about" className="mt-8 inline-flex items-center gap-2 font-semibold" data-testid="intro-about-link">
            <span className="link-underline">About the company</span> <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </div>
      <div className="lg:col-span-7 lg:pt-10">
        <BrandStructure />
      </div>
    </div>
  </section>
);

const Portfolio = () => (
  <section className="pb-24 md:pb-36" id="portfolio" data-testid="portfolio-section">
    <div className="container-x">
      <SectionHead
        eyebrow="02 — Brand Portfolio"
        lines={["Eight brands.", <em key="e" className="italic font-light text-sky-ink">Each with its own</em>, "purpose."]}
        intro="Every business in the Suvirika portfolio serves a distinct audience and business model — from trade distribution to beauty, lifestyle, apparel and household care."
      />
      <div className="mt-16">
        <BrandPortfolioGrid brands={BRANDS} />
      </div>
    </div>
  </section>
);

const Approach = () => (
  <section className="bg-[var(--paper-2)] py-24 md:py-36" data-testid="approach-section">
    <div className="container-x">
      <SectionHead
        eyebrow="03 — Business Approach"
        lines={["Distinct Brands.", <em key="c" className="italic font-light text-rose-ink">Connected</em>, "Opportunities."]}
        intro="Our brands operate independently, each at its own stage of development, while sharing a common commitment to practical commerce and everyday value."
      />
      <div className="mt-16"><BusinessPillars /></div>
      <Reveal className="mt-12">
        <Link to="/business-approach" className="btn-line" data-testid="approach-learn-more-btn">Our Business Approach <ArrowUpRight size={18} /></Link>
      </Reveal>
    </div>
  </section>
);

const Vision = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <section ref={ref} className="relative overflow-hidden bg-[var(--ink)] py-24 text-[var(--paper)] md:py-36 grain" data-testid="vision-section">
      <div className="container-x relative grid gap-14 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-7">
          <Reveal><p className="eyebrow !text-[#c9c5bd]">04 — Our Vision</p></Reveal>
          <MaskHeading lines={["Building for", <em key="i" className="italic font-light text-[var(--sky)]">Everyday India.</em>]} className="font-display mt-5 text-5xl leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl" />
          <Reveal delay={0.2}>
            <p className="mt-10 max-w-2xl text-lg leading-relaxed text-[#d9d5ce] md:text-xl">
              Our vision is to build a portfolio of trusted consumer brands and practical commerce solutions that connect products, businesses, and everyday needs. We aim to grow through focused execution, accessible offerings, meaningful partnerships, and distinct brand identities.
            </p>
          </Reveal>
          <Reveal delay={0.3} className="mt-10 grid max-w-2xl grid-cols-2 gap-px bg-white/15 sm:grid-cols-4">
            {["Focused execution", "Accessible offerings", "Meaningful partnerships", "Distinct identities"].map((t) => (
              <div key={t} className="bg-[var(--ink)] py-4 pr-4 text-sm text-[#d9d5ce]">{t}</div>
            ))}
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <div className="arch relative mx-auto aspect-[4/5] w-full max-w-md overflow-hidden">
            <motion.img src="/images/street-store.jpg" alt="A busy neighbourhood general store in India" loading="lazy" className="absolute inset-0 h-[124%] w-full object-cover" style={{ y, top: "-12%" }} />
          </div>
        </div>
      </div>
    </section>
  );
};

const PartnerCTA = () => (
  <section className="py-24 md:py-36" id="partner" data-testid="partner-section">
    <div className="container-x grid gap-16 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Reveal><p className="eyebrow">05 — Partner With Us</p></Reveal>
        <MaskHeading lines={["Let's Build", <em key="m" className="italic font-light text-sky-ink">Meaningful</em>, "Business Partnerships."]} className="font-display mt-5 text-4xl leading-[1.04] tracking-tight sm:text-5xl" />
        <Reveal delay={0.15}>
          <p className="mt-8 max-w-md leading-relaxed text-[var(--ink-2)] md:text-lg">
            We welcome conversations with manufacturers, distributors, retailers, suppliers, and potential business partners who share our interest in building sustainable consumer and commerce businesses.
          </p>
        </Reveal>
        <ul className="mt-10 border-t hairline">
          {ENQUIRY_TYPES.map((t, i) => (
            <Reveal as="li" key={t} delay={0.05 * i} className="flex items-baseline gap-5 border-b hairline py-4">
              <span className="font-mono-label text-xs text-[var(--ink-2)]">0{i + 1}</span>
              <span className="font-medium">{t}</span>
            </Reveal>
          ))}
        </ul>
      </div>
      <Reveal delay={0.1} className="lg:col-span-7 border hairline bg-white p-6 md:p-10">
        <EnquiryForm testId="home-enquiry-form" />
      </Reveal>
    </div>
  </section>
);

const ContactTeaser = () => (
  <section className="pb-24 md:pb-36" data-testid="contact-teaser">
    <div className="container-x">
      <SectionHead eyebrow="06 — Contact" lines={["Get in touch."]} intro="Official company contact details will appear here once verified." />
      <div className="mt-12"><ContactSection /></div>
    </div>
  </section>
);

export default function Home() {
  usePageMeta(null, "Suvirika Retail Pvt Ltd is an Indian multi-brand company building consumer and commerce businesses across B2B distribution, beauty, FMCG, fashion, accessories and household care.");
  return (
    <>
      <HeroSection />
      <Marquee />
      <Intro />
      <Portfolio />
      <Approach />
      <Vision />
      <PartnerCTA />
      <ContactTeaser />
    </>
  );
}
